// Cardholders.cshtml: search and filters, 20 a page. Unlike the legacy page (W19) the list shows as soon as the tab
// opens, and a filter applies when it is chosen: staff read an empty list and a Filter button that changed nothing
// as a filter that did not work. QOR staff can switch the box to a card reference number (CRN, a kiosk card's only
// sign-in to QBalance): that one is sent once by POST, never in the URL or the typeahead, and is cleared at once.
import { Icon, words } from '@qor/web-shared';
import { useQuery } from '@tanstack/react-query';
import { type ChangeEvent, type FormEvent, useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminApi, kioskApi, totalPages } from '../api';
import { usePageTitle } from '../components/ConsoleLayout';
import { toQuery, useFilterForm, useQueryParams } from '../components/filters';
import { useStaff } from '../components/RequireStaff';
import { SearchSuggest } from '../components/SearchSuggest';
import { ChooseProcessor, PageHeading, Pagination, StatusBadge } from '../components/ui';
import { clientOptions, effectiveProgramId, effectiveProvider, PROVIDER_OPTIONS, STATUS_OPTIONS } from '../scope';

const PAGE_SIZE = 20;

export function CardholdersPage() {
  usePageTitle('Cardholders');
  const { scope } = useStaff();
  const q = useQueryParams();
  const f = {
    search: q.get('search'),
    provider: q.get('provider'),
    status: q.get('status'),
    programId: q.get('program'),
  };
  const page = q.page();
  const submit = useFilterForm('/cardholders');
  // a filter applies as soon as it is chosen; the search box still waits for Enter or the button
  const applyNow = (e: ChangeEvent<HTMLSelectElement>) => e.currentTarget.form?.requestSubmit();

  const results = useQuery({
    queryKey: ['cardholders', scope.activeProvider, f, page],
    enabled: !scope.needsProvider,
    queryFn: () =>
      adminApi.cardholders({
        search: f.search,
        provider: effectiveProvider(scope, f.provider),
        status: f.status,
        programId: effectiveProgramId(scope, f.programId),
        page,
        pageSize: PAGE_SIZE,
      }),
  });
  const programs = useQuery({
    queryKey: ['programs'],
    enabled: !scope.needsProvider,
    queryFn: adminApi.programs,
  });
  // the typeahead (OnGetSuggestAsync): the processor in view, or the own client; no other filter
  const { internal, activeProvider, ownProgramId } = scope;
  const suggest = useCallback(
    async (text: string) =>
      (
        await adminApi.cardholders({
          search: text,
          provider: internal ? activeProvider : null,
          programId: internal ? null : ownProgramId,
          page: 1,
          pageSize: 6,
        })
      ).items,
    [internal, activeProvider, ownProgramId],
  );

  const navigate = useNavigate();
  const [by, setBy] = useState<'name' | 'crn'>('name');
  const [crnBusy, setCrnBusy] = useState(false);
  const [crnError, setCrnError] = useState<string | null>(null);
  async function findByCrn(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const crn = String(new FormData(form).get('cardReference') ?? '').trim();
    form.reset(); // the CRN does not stay on screen
    if (!crn) return;
    setCrnBusy(true);
    setCrnError(null);
    const r = await kioskApi.cardByReference(crn);
    setCrnBusy(false);
    if (r.ok) return navigate(`/cardholders/${r.data.cardholderId}`);
    setCrnError(
      r.code === 'TOO_MANY_REQUESTS'
        ? 'Too many lookups. Wait a minute and try again.'
        : r.code === 'NOT_FOUND'
          ? 'No card has this card reference number.'
          : r.error,
    );
  }
  const searchBy = scope.internal && (
    <select
      aria-label="Search by"
      value={by}
      onChange={(e) => {
        setBy(e.currentTarget.value as 'name' | 'crn');
        setCrnError(null);
      }}
      className="qc-search-by"
    >
      <option value="name">Name or email</option>
      <option value="crn">Card ref no.</option>
    </select>
  );

  const filtered = f.search !== null || f.provider !== null || f.status !== null || f.programId !== null;
  const data = results.data;

  return (
    <>
      <PageHeading
        title="Cardholders"
        actions={
          !scope.needsProvider && (
            <>
              <Link to="/cardholders/import" className="qc-button-ghost">
                <Icon name="upload" size={16} />
                Bulk Add
              </Link>
              <Link to="/cardholders/new" className="qor-button qor-button-primary">
                <Icon name="plus" size={16} />
                Add cardholder
              </Link>
            </>
          )
        }
      />

      {scope.needsProvider ? (
        <ChooseProcessor what="to see its cardholders" />
      ) : (
        <>
          {by === 'crn' ? (
            <>
              <form className="qc-cardholder-filters" onSubmit={(e) => void findByCrn(e)}>
                {searchBy}
                <div className="qc-search-wrap">
                  <input
                    name="cardReference"
                    placeholder="Card reference number"
                    aria-label="Card reference number"
                    autoComplete="off"
                    spellCheck={false}
                    maxLength={60}
                    required
                  />
                </div>
                <button type="submit" className="qor-button qor-button-primary" disabled={crnBusy}>
                  {crnBusy ? 'Finding…' : 'Find'}
                </button>
              </form>
              {crnError && (
                <p className="qc-auth-error" role="alert">
                  {crnError}
                </p>
              )}
            </>
          ) : (
            (!scope.internal || programs.isSuccess) && (
              <form className="qc-cardholder-filters" onSubmit={submit} key={toQuery(f)}>
                {searchBy}
                <SearchSuggest
                  id="cardholder-search-input"
                  defaultValue={f.search ?? ''}
                  placeholder="Search by name or email..."
                  suggest={suggest}
                  linkTo={(id) => `/cardholders/${id}`}
                />

                {scope.internal ? (
                  <select
                    name="program"
                    aria-label="Filter by client"
                    defaultValue={f.programId ?? ''}
                    onChange={applyNow}
                  >
                    <option value="">All clients</option>
                    {clientOptions(
                      (programs.data ?? []).filter((p) => p.provider !== 'ZumRails'),
                      scope,
                    ).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.displayName}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    name="provider"
                    aria-label="Filter by processor"
                    defaultValue={f.provider ?? ''}
                    onChange={applyNow}
                  >
                    <option value="">All processors</option>
                    {PROVIDER_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                )}

                <select name="status" aria-label="Filter by status" defaultValue={f.status ?? ''} onChange={applyNow}>
                  <option value="">All statuses</option>
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {words(o)}
                    </option>
                  ))}
                </select>

                <button type="submit" className="qor-button qor-button-primary">
                  Filter
                </button>
                {filtered && (
                  <Link className="qc-button-ghost" to="/cardholders">
                    Clear
                  </Link>
                )}
              </form>
            )
          )}

          {scope.internal && (
            <p className="qc-scope-label qc-list-note">
              Showing <strong>{scope.activeProvider}</strong> cardholders only
            </p>
          )}

          <div className="qc-panel">
            {!data ? null : data.items.length === 0 ? (
              <p className="qc-empty">No cardholders match these filters.</p>
            ) : (
              <>
                <div className="qc-table-scroll">
                  <table className="qc-plain-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Program</th>
                        <th>Processor</th>
                        <th>Status</th>
                        <th>
                          <span className="qc-visually-hidden">Open</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map((c) => (
                        <tr key={c.id}>
                          <td className="qc-cell-strong">{c.name}</td>
                          <td>{c.email}</td>
                          <td>{c.programName}</td>
                          <td>{c.provider}</td>
                          <td>
                            <StatusBadge status={c.status} />
                          </td>
                          <td className="qc-cell-action">
                            <Link to={`/cardholders/${c.id}`} aria-label={`View ${c.name}`}>
                              View
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={data.page}
                  totalPages={totalPages(data)}
                  totalCount={data.totalCount}
                  href={(p) =>
                    `/cardholders${toQuery({ search: f.search, provider: f.provider, status: f.status, program: f.programId, page: p })}`
                  }
                />
              </>
            )}
          </div>
        </>
      )}
    </>
  );
}
